import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { userAPI } from '../../../utils/api';
import { ASSIGNABLE_ROLE_OPTIONS, toRoleCode } from '../../../utils/roleUtils';
import Notifier from '../../../components/ui/Notifier';

const AddUserModal = ({ isOpen, onClose, onSuccess, recruiters = [] }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    username: '',
    email: '',
    password: '',
    role: 'clab',
    status: 'active',
    recruiter_id: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });

  const userRoles = ASSIGNABLE_ROLE_OPTIONS;

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        full_name: '',
        username: '',
        email: '',
        password: '',
        role: 'clab',
        status: 'active',
        recruiter_id: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const generateStrongPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    
    password += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
    password += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
    password += "0123456789"[Math.floor(Math.random() * 10)];
    password += "!@#$%^&*"[Math.floor(Math.random() * 8)];
    
    for (let i = 4; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  };

  const handleGeneratePassword = () => {
    const newPassword = generateStrongPassword();
    setFormData(prev => ({ ...prev, password: newPassword }));
    setShowPassword(true);
  };

  const validateForm = () => {
    const newErrors = {};

    if ((formData.role === 'clab' || formData.role === 'admin') && !formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required for CLAB and Master Admin users';
    }
    if (!formData.username.trim()) newErrors.username = 'Username is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }
    if (formData.role === 'recruiter' && !formData.recruiter_id) newErrors.recruiter_id = 'Recruiter assignment is required for recruiter role';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const trimmedFullName = formData.full_name?.trim();
      const trimmedUsername = formData.username.trim();
      const submitData = { 
        name: trimmedFullName || trimmedUsername || 'System User',
        username: trimmedUsername,
        email: formData.email,
        password: formData.password,
        status: formData.status,
        // Convert role to numeric format for API
        role: toRoleCode(formData.role)
      };

      if (formData.role === 'recruiter') {
        const selectedRecruiter = recruiters.find(r => r.id === parseInt(formData.recruiter_id));
        if (selectedRecruiter) {
          submitData.name = (selectedRecruiter.name || 'Recruiter User').trim();
          const recruiterId = parseInt(formData.recruiter_id, 10);
          if (!Number.isNaN(recruiterId)) {
            submitData.recruiterId = recruiterId;
          }
        }
      }

      const response = await userAPI.createUser(submitData);
      
      if (response?.success || response?.data) {
        setNotification({ show: true, type: 'success', message: `User created! Welcome email sent to ${submitData.email}` });
        setTimeout(() => {
          onSuccess?.(response.data || response);
          onClose();
        }, 1500);
      } else {
        const errorMessage = response?.message || 'Failed to create user.';
        setErrors({ general: errorMessage });
        setNotification({ show: true, type: 'error', message: errorMessage });
      }
    } catch (error) {
      const errorDetails = error.response?.data;
      console.error('Failed to create user', errorDetails || error);
      const errorMessage = errorDetails?.error || errorDetails?.message || error.message || 'An unexpected error occurred.';
      setErrors({ general: errorMessage });
      setNotification({ show: true, type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let newFormData = { ...formData, [name]: value };

    // Auto-populate full_name when a recruiter is selected
    if (name === 'recruiter_id') {
      const selectedRecruiter = recruiters.find(r => r.id === parseInt(value));
      if (selectedRecruiter) {
        newFormData.full_name = selectedRecruiter.name || '';
      } else {
        newFormData.full_name = '';
      }
    }

    // Clear recruiter_id when role changes from recruiter
    if (name === 'role' && value !== 'recruiter') {
      newFormData.recruiter_id = '';
      newFormData.full_name = ''; // Also clear full_name
    }

    setFormData(newFormData);
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-20">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between p-6 border-b border-secondary-200">
              <div>
                <h2 className="text-xl font-semibold text-text-primary">Add New User</h2>
                <p className="text-sm text-secondary-600 mt-1">Create a new system user (excludes employers)</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-secondary-100 rounded-lg transition-colors duration-150"
                disabled={loading}
              >
                <Icon name="X" size={20} className="text-secondary-600" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="flex-1 overflow-y-auto p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {errors.general && (
                  <div className="bg-error-50 border border-error-200 rounded-lg p-4">
                    <div className="flex items-center">
                      <Icon name="AlertCircle" size={20} className="text-error-600 mr-2" />
                      <span className="text-sm text-error-700">{errors.general}</span>
                    </div>
                  </div>
                )}

                {/* Role - Moved to top */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Role <span className="text-error-500">*</span>
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.role ? 'border-error-300' : 'border-secondary-300'
                    }`}
                    disabled={loading}
                  >
                    {userRoles.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                  {formData.role && (
                    <p className="mt-1 text-sm text-secondary-600">
                      {userRoles.find(r => r.value === formData.role)?.description}
                    </p>
                  )}
                  {errors.role && (
                    <p className="mt-1 text-sm text-error-600">{errors.role}</p>
                  )}
                </div>

                {/* Recruiter Assignment - Show first if recruiter role selected */}
                {formData.role === 'recruiter' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Assign to Recruiter <span className="text-error-500">*</span>
                    </label>
                    <select
                      name="recruiter_id"
                      value={formData.recruiter_id}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.recruiter_id ? 'border-error-300' : 'border-secondary-300'
                      }`}
                      disabled={loading}
                    >
                      <option value="">Select recruiter agency</option>
                      {recruiters && recruiters.length > 0 ? (
                        recruiters.map(recruiter => (
                          <option key={recruiter.id} value={recruiter.id}>
                            {recruiter.name || 'Unknown'} - {recruiter.country || 'Unknown'}
                          </option>
                        ))
                      ) : (
                        <option value="" disabled>No recruiters available</option>
                      )}
                    </select>
                    {errors.recruiter_id && (
                      <p className="mt-1 text-sm text-error-600">{errors.recruiter_id}</p>
                    )}
                  </div>
                )}

                {/* Full Name - Only show for Admin and CLAB roles */}
                {['admin', 'clab'].includes(formData.role) && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Full Name <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.full_name ? 'border-error-300' : 'border-secondary-300'
                      }`}
                      placeholder="Enter full name"
                      disabled={loading}
                    />
                    {errors.full_name && (
                      <p className="mt-1 text-sm text-error-600">{errors.full_name}</p>
                    )}
                  </div>
                )}

                {/* Show recruiter name for recruiter role */}
                {formData.role === 'recruiter' && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Full Name (Auto-filled from recruiter)
                    </label>
                    <input
                      type="text"
                      value={formData.full_name}
                      className="w-full px-3 py-2 border border-secondary-200 rounded-lg bg-secondary-50 text-secondary-600"
                      disabled
                    />
                    <p className="mt-1 text-sm text-secondary-600">
                      This will be automatically set as the full name
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Username <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.username ? 'border-error-300' : 'border-secondary-300'
                      }`}
                      placeholder="Enter username"
                      disabled={loading}
                    />
                    {errors.username && (
                      <p className="mt-1 text-sm text-error-600">{errors.username}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">
                      Email Address <span className="text-error-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.email ? 'border-error-300' : 'border-secondary-300'
                      }`}
                      placeholder="Enter email address"
                      disabled={loading}
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-error-600">{errors.email}</p>
                    )}
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Password <span className="text-error-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 pr-20 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                        errors.password ? 'border-error-300' : 'border-secondary-300'
                      }`}
                      placeholder="Enter password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-10 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                    >
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    disabled={loading}
                  >
                    Generate Strong Password
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-error-600">{errors.password}</p>
                  )}
                </div>

                {/* Initial Status */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Initial Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    disabled={loading}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <p className="mt-1 text-sm text-secondary-600">
                    {formData.status === 'active' 
                      ? 'Active users can log in immediately after creation'
                      : 'Inactive users will need to be activated before they can log in'
                    }
                  </p>
                </div>

                {/* Email Notification Info */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Icon name="Mail" size={20} className="text-primary-600 mr-2 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-primary-800">Email Notification</h4>
                      <p className="text-sm text-primary-700 mt-1">
                        A welcome email with login credentials will be automatically sent to the user's email address.
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {/* Fixed Footer */}
            <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                    Creating User...
                  </>
                ) : (
                  <>
                    <Icon name="UserPlus" size={16} className="mr-2" />
                    Create User
                  </>
                )}
              </button>
            </div>
        </div>

        {/* Notification */}
        <Notifier
          show={notification.show}
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification({ show: false, type: '', message: '' })}
        />
    </div>
  );
};

export default AddUserModal;
